(() => {
  if (customElements.get('es-personalization')) return;

  class Personalization extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.form = this.closest('form');
      this.fields = this.querySelector('[data-es-fields]');
      this.status = this.querySelector('[data-es-status]');
      this.button = this.form?.querySelector('[data-es-personalization-button]');
      this.values = new Map();
      try {
        this.config = JSON.parse(this.querySelector('[data-es-config]').textContent);
        this.strings = JSON.parse(this.querySelector('[data-es-strings]').textContent);
        this.availability = JSON.parse(this.querySelector('[data-es-availability]').textContent);
        if (!this.form || !this.button) throw new Error('Missing product form');
        this.update();
        this.form.addEventListener('change', () => {
          requestAnimationFrame(() => this.update());
        }, { signal: this.controller.signal });
        this.form.addEventListener('submit', event => {
          this.update();
          this.fields.querySelectorAll('input[type="text"]').forEach(input => {
            input.value = input.value.trim();
            this.validateInput(input);
          });
          if (this.failed || !this.form.checkValidity()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.form.reportValidity();
          }
        }, { capture: true, signal: this.controller.signal });
      } catch (error) {
        this.fail();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    fail() {
      this.failed = true;
      this.status.hidden = false;
      this.status.textContent = this.strings?.unavailable || this.status.textContent;
      this.fields.replaceChildren();
      if (this.button) this.button.disabled = true;
    }

    update() {
      const selector = this.form.querySelector('select[name="id"], input[name="id"]');
      const variantId = selector?.value || this.dataset.variant;
      if (variantId === this.currentVariant) return;
      this.fields.querySelectorAll('[data-es-value]').forEach(input => {
        this.values.set(input.dataset.esValue, input.value);
      });
      this.currentVariant = variantId;
      const definitions = this.config.variants[variantId];
      if (!definitions) return this.fail();
      this.failed = false;
      const fragment = document.createDocumentFragment();
      for (const field of definitions) fragment.append(this.createField(field));
      this.fields.replaceChildren(fragment);
      this.status.hidden = true;
      this.button.disabled = !this.availability[variantId];
    }

    validateInput(input) {
      input.setCustomValidity('');
      if (input.maxLength > 0 && input.value.length > input.maxLength) {
        input.setCustomValidity(this.strings.max_chars.replace('__count__', input.maxLength));
      } else if (input.value && input.minLength > 0 && input.value.length < input.minLength) {
        input.setCustomValidity(this.strings.min_chars.replace('__count__', input.minLength));
      }
    }

    createField(field) {
      const wrapper = document.createElement('div');
      wrapper.className = 'es-personalization__field';
      const label = document.createElement('label');
      const id = `${this.id}-${this.currentVariant}-${field.key}`;
      label.htmlFor = id;
      const number = /\d+$/.test(field.label) ? ` ${field.number}` : '';
      label.textContent = `${this.strings[field.kind]}${number}`;
      if (!field.required) {
        const optional = document.createElement('span');
        optional.className = 'es-personalization__optional';
        optional.textContent = ` (${this.strings.optional})`;
        label.append(optional);
      }
      wrapper.append(label);
      const input = document.createElement(field.options.length ? 'select' : 'input');
      input.id = id;
      input.name = `properties[${field.label}]`;
      input.required = field.required;
      const cacheKey = `${field.kind}-${field.number}`;
      input.dataset.esValue = cacheKey;
      if (field.options.length) {
        input.add(new Option(this.strings.choose, ''));
        field.options.forEach((value, index) => {
          let text = value;
          if (field.kind === 'birthstone') {
            const month = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'][index];
            text = this.strings[`month_${month}`];
          } else if (field.kind === 'charm') {
            text = this.strings[value.toLowerCase()] || value;
          }
          input.add(new Option(text, value));
        });
      } else {
        input.type = 'text';
        input.autocomplete = 'off';
        input.spellcheck = false;
        if (field.max > 0) input.maxLength = field.max;
        if (field.min > 0) input.minLength = field.min;
        if (field.kind === 'date') input.placeholder = 'DD.MM.JJJJ';
      }
      if (this.values.has(cacheKey)) input.value = this.values.get(cacheKey);
      input.addEventListener('input', () => this.validateInput(input));
      input.addEventListener('change', () => this.validateInput(input));
      this.validateInput(input);
      input.addEventListener('invalid', () => {
        if (input.validity.valueMissing) input.setCustomValidity(this.strings.required);
      });
      wrapper.append(input);
      if (field.max > 0) {
        const hint = document.createElement('small');
        hint.className = 'es-personalization__hint';
        hint.id = `${id}-hint`;
        hint.textContent = this.strings.max_chars.replace('__count__', field.max);
        input.setAttribute('aria-describedby', hint.id);
        wrapper.append(hint);
      }
      return wrapper;
    }
  }

  customElements.define('es-personalization', Personalization);
})();
